import React, { useRef, useEffect, useContext, useCallback } from 'react'
import PropTypes from 'prop-types'
import Helmet from 'react-helmet'
import ImagesLoaded from 'imagesloaded'

// Components
import { StoreDispatch } from '../../Store'
import Scroll from '../Scroll'
import TransitionMask from '../PageTransitionMask'
import Gridlines from '../Gridlines'
import Header from '../Header'

// Fonts
import { font } from '../../styles/vars/font.style'
import GTAmericaCompressedMediumttf from '../../fonts/gtamerica/GT-America-Compressed-Medium.ttf'
import GTAmericaCompressedMediumwoff from '../../fonts/gtamerica/GT-America-Compressed-Medium.woff'
import GTAmericaCompressedMediumwoff2 from '../../fonts/gtamerica/GT-America-Compressed-Medium.woff2'
import GTAmericaMediumttf from '../../fonts/gtamerica/GT-America-Standard-Medium.ttf'
import GTAmericaMediumwoff from '../../fonts/gtamerica/GT-America-Standard-Medium.woff'
import GTAmericaMediumwoff2 from '../../fonts/gtamerica/GT-America-Standard-Medium.woff2'
import GTAmericaRegularttf from '../../fonts/gtamerica/GT-America-Standard-Regular.ttf'
import GTAmericaRegularwoff from '../../fonts/gtamerica/GT-America-Standard-Regular.woff'
import GTAmericaRegularwoff2 from '../../fonts/gtamerica/GT-America-Standard-Regular.woff2'
import PPEditorialeot from '../../fonts/ppeditorial/PPEditorialNew-LightItalic.eot'
import PPEditorialttf from '../../fonts/ppeditorial/PPEditorialNew-LightItalic.ttf'
import PPEditorialwoff from '../../fonts/ppeditorial/PPEditorialNew-LightItalic.woff'
import PPEditorialwoff2 from '../../fonts/ppeditorial/PPEditorialNew-LightItalic.woff2'

// Styles
import { GlobalStyles } from '../../styles/GlobalStyles.style'
import ExpeditionsMask from '../ExpeditionsMask'

const Layout = ({ children, location }) => {
  const dispatch = useContext(StoreDispatch)

  const scrollContainerRef = useRef(null)

  const handleBrowserNavigationInteraction = useCallback(
    e => {
      dispatch({
        type: 'UPDATE_PATHNAME',
        payload: window.location.pathname,
      })
      dispatch({ type: 'UPDATE_POP_STATE' })
    },
    [dispatch]
  )

  const setViewportHeight = () => {
    const vh = window.innerHeight * 0.01
    document.documentElement.style.setProperty('--vh', `${vh}px`)
  }

  const handleAllImagesLoaded = () => {
    window.scroll.update && window.scroll.update()
  }

  useEffect(() => {
    const imageLoadTrack = new ImagesLoaded(scrollContainerRef.current)

    imageLoadTrack.on('always', handleAllImagesLoaded)

    return () => {
      imageLoadTrack.off('always', handleAllImagesLoaded)
    }
  }, [location])

  useEffect(() => {
    setViewportHeight()

    window.addEventListener('resize', setViewportHeight)
    window.addEventListener('popstate', handleBrowserNavigationInteraction)

    return () => {
      window.removeEventListener('resize', setViewportHeight)
      window.removeEventListener('popstate', handleBrowserNavigationInteraction)
    }
  }, [dispatch, handleBrowserNavigationInteraction])

  useEffect(() => {
    // Reset scroll on reload
    window.onbeforeunload = window.scrollTo(0, 0)
  }, [])

  const getCookie = cname => {
    let name = cname + '='
    let decodedCookie = decodeURIComponent(document.cookie)
    let ca = decodedCookie.split(';')
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i]
      while (c.charAt(0) === ' ') {
        c = c.substring(1)
      }
      if (c.indexOf(name) === 0) {
        return c.substring(name.length, c.length)
      }
    }
    return ''
  }

  // Handle country cookie
  useEffect(() => {
    const result = getCookie('country')
    if (result) {
      dispatch({
        type: 'UPDATE_COUNTRY_COOKIE',
        payload: result,
      })
    }
  }, [dispatch])

  return (
    <>
      <GlobalStyles />
      <Helmet>
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=0"
        />
        <script charset="UTF-8">
          {`window['adrum-start-time'] = new Date().getTime();
            (function(config) {
              config.appKey = '${process.env.GATSBY_APP_DYNAMICS_KEY}';
                config.adrumExtUrlHttp = 'http://cdn.appdynamics.com';
                config.adrumExtUrlHttps = 'https://cdn.appdynamics.com';
                config.beaconUrlHttp = 'http://pdx-col.eum-appdynamics.com';
                config.beaconUrlHttps = 'https://pdx-col.eum-appdynamics.com';
                config.xd = {
                  enable: false 
                };
                config.spa = {
                  "spa2": true
                 };
                config.navComplete = {
                  maxResourceQuietTime: 500,
                  maxXhrQuietTime: 500,
                  maxInactiveTime: 500  };
                  
                var pageName = "";
                //set error page names
                switch (window.document.title) {
                  case "Page Not Found | Royal Caribbean Cruises":
                    pageName = "404 Error Page";
                    break;
                  case "Server Error | Royal Caribbean Cruises":
                    pageName = "5xx Error Page";
                    break;
                }
                
                function getCookieValue(cookieName) {
                  try {
                    var value = "; " + document.cookie;
                    var parts = value.split("; " + cookieName + "=");
                    if (parts.length == 2) {
                      return parts          .pop()
                        .split(";")
                        .shift();
                    } else {
                      return "";
                    }
                  } catch (err) {
                    return "error";
                  }
                }
                 
                //send data to appd
              config.userEventInfo = {
                  PageView: function(context) {
                    return {
                      userPageName: pageName,
                      userData: {
                        BlueGreenValue: getCookieValue("bgv"),
                        MarketingCloudID: getCookieValue("MCMID"),
                        LoyaltyID: getCookieValue("loyalty_ID"),
                        JSession: getCookieValue("jsess"),
                        BookingID: getCookieValue("booking_ID")
                      },
                      userDataLong: {
                        cookieSize: encodeURIComponent('<q></q>' + document.cookie).length}
                    };
                  },
                  Ajax: function (context) {
                    return {
                      userData: {
                        BlueGreenValue: getCookieValue("bgv"),
                        MarketingCloudID: getCookieValue("MCMID"),
                        LoyaltyID: getCookieValue("loyalty_ID"),
                        JSession: getCookieValue("jsess"),
                        BookingID: getCookieValue("booking_ID")
                      },
                      userDataLong: {
                        cookieSize: encodeURIComponent('<q></q>' + document.cookie).length}
                    };
                  },
                  VPageView: function (context) {
                    return {
                      userData: {
                        BlueGreenValue: getCookieValue("bgv"),
                        MarketingCloudID: getCookieValue("MCMID"),
                        LoyaltyID: getCookieValue("loyalty_ID"),
                        JSession: getCookieValue("jsess"),
                        BookingID: getCookieValue("booking_ID")
                      },
                      userDataLong: {
                        cookieSize: encodeURIComponent('<q></q>' + document.cookie).length
                        }
                    };
                  }
                };
              })
              (window['adrum-config'] || (window['adrum-config'] = {}));`}
        </script>
        <script src="https://cdnjs.cloudflare.com/ajax/libs/babel-polyfill/6.26.0/polyfill.min.js" />
        <script src="//cdn.appdynamics.com/adrum/adrum-latest.js?_=3.34.0"></script>

        <style>{`
          @font-face {
            font-family: 'GTAmericaCompressed';
            src: url(${GTAmericaCompressedMediumwoff2}) format('woff2'),
              url(${GTAmericaCompressedMediumwoff}) format('woff'),
              url(${GTAmericaCompressedMediumttf}) format('truetype');
            font-weight: ${font.heading.weight.medium};
          }
          @font-face {
            font-family: 'PPEditorial';
            src: url(${PPEditorialeot}) format('eot'),
              url(${PPEditorialwoff2}) format('woff2'),
              url(${PPEditorialwoff}) format('woff'),
              url(${PPEditorialttf}) format('truetype');
            font-weight: ${font.headingAlt.weight.light};
          }
          @font-face {
            font-family: 'GTAmerica';
            src: url(${GTAmericaMediumwoff2}) format('woff2'),
              url(${GTAmericaMediumwoff}) format('woff'),
              url(${GTAmericaMediumttf}) format('truetype');
            font-weight: ${font.primary.weight.medium};
          }
          @font-face {
            font-family: 'GTAmerica';
            src: url(${GTAmericaRegularwoff2}) format('woff2'),
              url(${GTAmericaRegularwoff}) format('woff'),
              url(${GTAmericaRegularttf}) format('truetype');
            font-weight: ${font.primary.weight.regular};
          }
        `}</style>
      </Helmet>

      <Gridlines show={false} />

      <Scroll callbacks={location} />

      <div id="scroll-container" data-scroll-container ref={scrollContainerRef}>
        <Header pathname={location.pathname} />
        {children}
        {/* <CookieBar /> */}
      </div>

      <ExpeditionsMask />
      <TransitionMask />
    </>
  )
}

Layout.propTypes = {
  children: PropTypes.node.isRequired,
}

export default Layout
